import type { Leg } from '../../state';
import { OrderAction } from '../../state/types';
import { effect, signal, computed, untracked } from '@preact/signals-react';
import type { ReadonlySignal, Signal } from '@preact/signals-react';
import { instrumentToString, parseInstrument } from 'src/lib/instrumentUtils';
import type { MarketDataService, MarketDataSubscription } from 'src/services/MarketDataService';

/**
 * Public readable market data state
 */
export interface MultiLegMarketDataState {
    isLoading: boolean;
    bidPrice: number | null;
    midPrice: number | null;
    askPrice: number | null;
    totalPrice: number | null;
}

interface SubscriptionEntry {
    instrumentString: string;
    subscription: MarketDataSubscription | 'pending' | null;
}

/**
 * Class to manage multi-leg market data subscriptions and updates
 */
export class MultiLegMarketData implements Disposable {
    #disposableStack = new DisposableStack();
    #marketDataService: MarketDataService;
    #legsSignal: ReadonlySignal<Leg[]>;

    // Store subscriptions as a mutable signal, keyed by leg ID
    // Each entry tracks the instrument and its subscription
    #subscriptions: Signal<Map</* legId */ string, SubscriptionEntry>>;

    // Computed state based on subscriptions and legs
    #state: ReadonlySignal<MultiLegMarketDataState>;

    // Computed signals for individual properties
    #askPrice: ReadonlySignal<number | null>;
    #bidPrice: ReadonlySignal<number | null>;
    #midPrice: ReadonlySignal<number | null>;
    #totalPrice: ReadonlySignal<number | null>;
    #isLoading: ReadonlySignal<boolean>;
    #legPrices: Signal<Record<string, number>>;

    constructor(marketDataService: MarketDataService, legsSignal: ReadonlySignal<Leg[]>) {
        this.#marketDataService = marketDataService;
        this.#legsSignal = legsSignal;
        this.#subscriptions = signal(new Map());
        this.#legPrices = signal({});

        // Computed state that calculates prices from subscriptions
        this.#state = computed(() => {
            const legs = this.#legsSignal.value;
            const subscriptions = this.#subscriptions.value;

            // If there are no legs at all, show ready state with null prices
            if (legs.length === 0) {
                this.#legPrices.value = {};
                return {
                    isLoading: false,
                    bidPrice: null,
                    midPrice: null,
                    askPrice: null,
                    totalPrice: null,
                };
            }

            // Only consider legs with valid instruments (non-null entries)
            // Check if all valid subscriptions are ready and connected
            let hasAnyValidLegs = false;
            let allValidLegsReady = true;

            for (const leg of legs) {
                const entry = subscriptions.get(leg.id);
                // Skip legs without valid instruments (null subscription)
                if (!entry || entry.subscription === null) continue;

                hasAnyValidLegs = true;

                // Check if subscription is pending or not connected
                if (entry.subscription === 'pending' || !entry.subscription.isConnected.value) {
                    allValidLegsReady = false;
                    break;
                }
            }

            if (!hasAnyValidLegs) {
                this.#legPrices.value = {};
                return {
                    isLoading: false,
                    bidPrice: null,
                    midPrice: null,
                    askPrice: null,
                    totalPrice: null,
                };
            }

            if (!allValidLegsReady) {
                this.#legPrices.value = {};
                return {
                    isLoading: true,
                    bidPrice: null,
                    midPrice: null,
                    askPrice: null,
                    totalPrice: null,
                };
            }

            // Calculate overall prices
            let overallBid = 0;
            let overallAsk = 0;
            let totalPrice = 0;
            const legPricesRecord: Record<string, number> = {};

            for (const leg of legs) {
                const entry = subscriptions.get(leg.id);
                // Skip legs without valid instruments or pending subscriptions
                if (!entry || entry.subscription === null || entry.subscription === 'pending') continue;

                const marketData = entry.subscription.marketData.value;
                if (marketData.bid === undefined || marketData.ask === undefined) {
                    // Not ready yet
                    this.#legPrices.value = {};
                    return {
                        isLoading: true,
                        bidPrice: null,
                        midPrice: null,
                        askPrice: null,
                        totalPrice: null,
                    };
                }

                const ratio = leg.quantityRatio || 1;
                const isEquity = leg.symbolPickerState.instrument.instrumentType === 'equity';
                // For equity legs, divide by 100 to show price per share (ratio is in shares, not lots)
                const equityDivisor = isEquity ? 100 : 1;

                const priceToUse = leg.side === OrderAction.Buy ? marketData.ask : marketData.bid;
                const legCost = (priceToUse * ratio) / equityDivisor;

                if (leg.side === OrderAction.Buy) {
                    // When buying, we pay the ask price
                    overallAsk += (marketData.ask * ratio) / equityDivisor;
                    // When selling (closing), we receive the bid price
                    overallBid += (marketData.bid * ratio) / equityDivisor;
                    // Calculate total price
                    totalPrice += legCost;
                    // Store per-leg cost (positive for buy)
                    legPricesRecord[leg.id] = legCost;
                } else {
                    // When selling, we receive the bid price
                    overallBid -= (marketData.bid * ratio) / equityDivisor;
                    // When buying back (closing), we pay the ask price
                    overallAsk -= (marketData.ask * ratio) / equityDivisor;
                    // Calculate total price
                    totalPrice -= legCost;
                    // Store per-leg cost (negative for sell)
                    legPricesRecord[leg.id] = -legCost;
                }
            }

            // Update per-leg prices signal
            this.#legPrices.value = legPricesRecord;

            return {
                isLoading: false,
                bidPrice: overallBid,
                midPrice: (overallBid + overallAsk) / 2,
                askPrice: overallAsk,
                totalPrice,
            };
        });

        // Create computed signals for individual properties (computed once, not on every access)
        this.#askPrice = computed(() => this.#state.value.askPrice);
        this.#bidPrice = computed(() => this.#state.value.bidPrice);
        this.#midPrice = computed(() => this.#state.value.midPrice);
        this.#totalPrice = computed(() => this.#state.value.totalPrice);
        this.#isLoading = computed(() => this.#state.value.isLoading);

        // Watch for leg changes and manage subscriptions
        this.#disposableStack.defer(
            effect(() => {
                const legs = this.#legsSignal.value;
                const currentLegIds = new Set(legs.map((leg) => leg.id));
                const subscriptions = this.#subscriptions.peek();

                untracked(() => {
                    // Remove subscriptions for legs that no longer exist
                    for (const [legId, entry] of subscriptions) {
                        if (!currentLegIds.has(legId)) {
                            if (entry.subscription !== 'pending' && entry.subscription !== null) {
                                entry.subscription[Symbol.dispose]();
                            }
                            subscriptions.delete(legId);
                        }
                    }

                    // Add or update subscriptions for legs
                    for (const leg of legs) {
                        const symbol = leg.symbolPickerState.symbolString;

                        // No symbol or invalid symbol - set to null
                        if (!symbol) {
                            const existingEntry = subscriptions.get(leg.id);
                            // Dispose existing subscription if present
                            if (
                                existingEntry?.subscription &&
                                existingEntry.subscription !== 'pending' &&
                                existingEntry.subscription !== null
                            ) {
                                existingEntry.subscription[Symbol.dispose]();
                            }
                            // Set to null to indicate no valid instrument
                            subscriptions.set(leg.id, {
                                instrumentString: '',
                                subscription: null,
                            });
                            this.#subscriptions.value = new Map(subscriptions);
                            continue;
                        }

                        // Parse the instrument to get its string representation
                        let instrumentString: string;
                        try {
                            const instrument = parseInstrument(symbol);
                            instrumentString = instrumentToString(instrument);
                        } catch {
                            // Invalid instrument - set to null
                            const existingEntry = subscriptions.get(leg.id);
                            // Dispose existing subscription if present
                            if (
                                existingEntry?.subscription &&
                                existingEntry.subscription !== 'pending' &&
                                existingEntry.subscription !== null
                            ) {
                                existingEntry.subscription[Symbol.dispose]();
                            }
                            subscriptions.set(leg.id, {
                                instrumentString: '',
                                subscription: null,
                            });
                            this.#subscriptions.value = new Map(subscriptions);
                            continue;
                        }

                        // Check if we need a new subscription (new leg or instrument changed)
                        const existingEntry = subscriptions.get(leg.id);
                        const needsNewSubscription =
                            !existingEntry || existingEntry.instrumentString !== instrumentString;

                        if (needsNewSubscription) {
                            // Dispose old subscription if it exists and is not pending/null
                            if (
                                existingEntry?.subscription &&
                                existingEntry.subscription !== 'pending' &&
                                existingEntry.subscription !== null
                            ) {
                                existingEntry.subscription[Symbol.dispose]();
                            }

                            // Mark as pending with the new instrument string
                            subscriptions.set(leg.id, {
                                instrumentString,
                                subscription: 'pending',
                            });
                            this.#subscriptions.value = new Map(subscriptions);

                            // Create subscription asynchronously
                            void this.#createSubscription(leg.id, instrumentString);
                        }
                    }
                });
            }),
        );
    }

    async #createSubscription(legId: string, instrumentString: string) {
        try {
            const instrument = parseInstrument(instrumentString);
            const subscription = await this.#marketDataService.subscribe(instrument);

            // Check if this leg still exists and still wants this instrument
            const subscriptions = this.#subscriptions.peek();
            const entry = subscriptions.get(legId);
            if (entry && entry.subscription === 'pending' && entry.instrumentString === instrumentString) {
                subscriptions.set(legId, {
                    instrumentString,
                    subscription,
                });
                this.#subscriptions.value = new Map(subscriptions);
            } else if (subscription) {
                // Leg was removed or instrument changed while we were subscribing, dispose immediately
                subscription[Symbol.dispose]();
            }
        } catch (_error) {
            // Handle subscription error - remove pending state
            const subscriptions = this.#subscriptions.peek();
            subscriptions.delete(legId);
            this.#subscriptions.value = new Map(subscriptions);
        }
    }

    // Public getters
    get askPrice(): ReadonlySignal<number | null> {
        return this.#askPrice;
    }

    get bidPrice(): ReadonlySignal<number | null> {
        return this.#bidPrice;
    }

    get midPrice(): ReadonlySignal<number | null> {
        return this.#midPrice;
    }

    get totalPrice(): ReadonlySignal<number | null> {
        return this.#totalPrice;
    }

    get isLoading(): ReadonlySignal<boolean> {
        return this.#isLoading;
    }

    get legPrices(): ReadonlySignal<Record<string, number>> {
        return this.#legPrices;
    }

    [Symbol.dispose]() {
        // Dispose all subscriptions
        for (const entry of this.#subscriptions.value.values()) {
            if (entry.subscription !== 'pending' && entry.subscription !== null) {
                entry.subscription[Symbol.dispose]();
            }
        }

        this.#disposableStack.dispose();
    }
}
